/**
 * Brand glyphs lucide does not ship, drawn once and shared.
 *
 * They live here rather than beside either consumer because the calendar's
 * platform picker and the settings Connections panel need the same marks, and
 * the second copy of the TikTok path had already drifted into being a duplicate.
 */
import type { ReactNode } from "react";

/** lucide-react ships no TikTok brand icon, so we inline the glyph. */
export function TikTokIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className={className}>
      <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
    </svg>
  );
}

/**
 * lucide ships only Linkedin out of the newer platforms, so the rest are drawn
 * here in lucide's own language (24 grid, stroke-1.75, round caps) rather than
 * dropped in as filled brand marks — at 14px a matching stroke weight is what
 * makes the row read as one set, not the exact brand path.
 */
export function StrokeGlyph({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      {children}
    </svg>
  );
}

/** Ghost silhouette. */
export function SnapchatIcon({ className }: { className?: string }) {
  return (
    <StrokeGlyph className={className}>
      <path d="M6 14V8.6a6 6 0 0 1 12 0V14c0 1.2 1 2 2.2 2.4.5.2.5.9 0 1.1-1 .5-2 .6-2.5 1.2-.4.5-.2 1.2-.9 1.3-.8.1-1.6-.4-2.4 0-.7.3-1.2 1.1-2.4 1.1s-1.7-.8-2.4-1.1c-.8-.4-1.6.1-2.4 0-.7-.1-.5-.8-.9-1.3-.5-.6-1.5-.7-2.5-1.2-.5-.2-.5-.9 0-1.1C5 16 6 15.2 6 14Z" />
    </StrokeGlyph>
  );
}

/** The alien head. */
export function RedditIcon({ className }: { className?: string }) {
  return (
    <StrokeGlyph className={className}>
      <ellipse cx="12" cy="14" rx="9" ry="6" />
      <path d="M13.9 8.2 15.5 4.3" />
      <circle cx="16.4" cy="3.7" r="1.3" />
      <circle cx="8.9" cy="13.2" r=".9" fill="currentColor" stroke="none" />
      <circle cx="15.1" cy="13.2" r=".9" fill="currentColor" stroke="none" />
      <path d="M9.1 16.4c1.8 1.2 4 1.2 5.8 0" />
    </StrokeGlyph>
  );
}

/** The P in its circle. */
export function PinterestIcon({ className }: { className?: string }) {
  return (
    <StrokeGlyph className={className}>
      <circle cx="12" cy="12" r="9" />
      <path d="M9.9 17.6V7.4h3.2a2.6 2.6 0 0 1 0 5.3H9.9" />
    </StrokeGlyph>
  );
}

/** Two bars over the notched flag. */
export function SubstackIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className={className}>
      <path d="M3.5 3.5h17v2.9h-17V3.5Zm0 5.3h17v2.9h-17V8.8Zm0 5.3h17v6.9L12 17.3l-8.5 3.7v-6.9Z" />
    </svg>
  );
}
